import { useSubmitMutation } from "@/src/services/api/auth.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { router } from "expo-router";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { z } from "zod";

const forgotPasswordSchema = z.object({
  email: z.string().min(1, "ERROR_NO_EMAIL").email("ERROR_EMAIL_NOT_VALID"),
});

export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export type UseForgotPasswordOptions = Readonly<{
  /**
   * Where the caller lives decides what "done" means.
   *
   * The unauthenticated Forgot Password screen sends the mail and then hands the
   * user back to /login — there is nowhere else for them to go. Settings ->
   * Change Password reuses the same request from inside the app, where that
   * redirect reads as being thrown out of the club: the session is still valid,
   * the user never asked to leave, and the reset link arrives by mail anyway.
   * There, the outcome belongs in a toast and the user stays put.
   */
  redirectToLogin?: boolean;
  /**
   * Overrides the success toast. The IdP's own message is fine on the Forgot
   * Password screen, but inside Settings it reads as a stray API response —
   * callers there state plainly what happened instead.
   */
  successMessage?: string;
}>;

export const useForgotPassword = ({
  redirectToLogin = true,
  successMessage,
}: UseForgotPasswordOptions = {}) => {
  const { t } = useTranslation();

  const form = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError: setFormError,
  } = form;

  const submitMutation = useSubmitMutation({
    onSuccess: async (data) => {
      const responseMessage =
        typeof data?.message === "string"
          ? data.message
          : data?.message?.error_description ||
            data?.message?.error ||
            t("SUCCESS_REQUEST_SUBMITTED");

      Toast.show({
        type: "success",
        text1: successMessage ?? responseMessage,
      });
      if (redirectToLogin) router.replace("/login");
    },
    onError: (err: any) => {
      const status = err.response?.status;
      const responseMessage = err.response?.data;
      const errorMessage =
        typeof responseMessage === "string"
          ? responseMessage
          : responseMessage?.error_description ||
            responseMessage?.errors?.Email?.replace("_", " ");

      const fallbackKey =
        status === 400 || status === 422
          ? "ERROR_INVALID_REQUEST"
          : status === 403
            ? "ERROR_FORBIDDEN"
            : status === 404
              ? "ERROR_NOT_FOUND"
              : status === 500
                ? "ERROR_SERVER"
                : "ERROR_SUBMIT_FAILED";

      // A 500 says nothing the user can act on, so its body is not shown.
      const message =
        status === 500 ? t("ERROR_SERVER") : errorMessage || t(fallbackKey);

      setFormError("email", { message });

      // Callers that stay on their own screen (Settings) never render this
      // form, so a field-level error would be written somewhere invisible and
      // the button would just stop spinning with no explanation.
      if (!redirectToLogin) {
        Toast.show({ type: "error", text1: message });
      }
    },
  });

  const onSubmit = (data: ForgotPasswordFormData) => {
    submitMutation.mutate({ email: data.email });
  };


  return {
    control,
    handleSubmit,
    errors,

    isSubmitting: submitMutation.isPending,

    onSubmit,

    t,
  };
};
